import {
  isEarlyFunnelProjectPhase,
  type DependencyEdgeType,
  type OnboardingItem,
  type OnboardingTemplateItem,
  type ProjectOnboarding,
  type ProjectPhase,
  type TaskDependencyEdge,
  type TaskGroup,
  type TimberProject,
} from '../types';

/**
 * Shared staff-only planning prefs (Firestore).
 * Doc: `staffPlanning/hub` — employee/admin read+write only (see firestore.rules).
 * Never put phase / contract / proposal / money fields here.
 */
export const STAFF_PLANNING_COLLECTION = 'staffPlanning' as const;
export const STAFF_PLANNING_HUB_DOC_ID = 'hub' as const;

/** @deprecated Cleared on load; preference now lives in Firestore `staffPlanning/hub`. */
export const SIMULATE_UNDER_CONTRACT_STORAGE_KEY = 'pm.staffPlanning.simulateUnderContract';

export const SIM_UC_GROUP_ID_PREFIX = '__sim_uc_group__:';

/** Default UC task groups injected for planning views when missing on early-funnel projects. */
export const SIMULATED_UC_GROUP_DEFS = [
  { key: 'design', name: 'Design', color: '#9F7AEA', position: 10 },
  { key: 'procurement', name: 'Procurement', color: '#4299E1', position: 20 },
  { key: 'production', name: 'Production', color: '#ED8936', position: 30 },
  { key: 'installation', name: 'Installation', color: '#38B2AC', position: 40 },
] as const;

/**
 * Dates and dependency links saved while simulating a checklist row.
 * Stored on `staffPlanning/hub` — never on `projectOnboarding`.
 */
export type SimulatedScheduleItem = {
  startMs?: number;
  endMs?: number;
  dependencyEdges?: TaskDependencyEdge[];
};

/** projectId → onboarding item id → saved simulation dates. */
export type SimulatedSchedulesByProject = Record<string, Record<string, SimulatedScheduleItem>>;

/** Shape of `staffPlanning/hub` (staff overlay prefs only). */
export type StaffPlanningHubDoc = {
  /** @deprecated Was a global all-projects switch. Ignored; use project IDs. */
  simulateUnderContract?: boolean;
  simulatedUnderContractProjectIds?: string[];
  simulatedSchedules?: SimulatedSchedulesByProject;
  updatedAt?: unknown;
  updatedBy?: string;
  updatedByName?: string;
};

export function parseSimulatedUnderContractProjectIds(data: unknown): string[] {
  if (!data || typeof data !== 'object') return [];
  const raw = (data as StaffPlanningHubDoc).simulatedUnderContractProjectIds;
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const id = item.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function parseSimulateUnderContractEnabled(data: unknown): boolean {
  return parseSimulatedUnderContractProjectIds(data).length > 0;
}

function simulatedIdSet(
  ids: ReadonlySet<string> | readonly string[] | undefined,
): ReadonlySet<string> {
  if (!ids) return new Set();
  return ids instanceof Set ? ids : new Set(ids);
}

function isSelectedForSimulation(
  projectId: string | undefined | null,
  ids: ReadonlySet<string> | readonly string[] | undefined,
): boolean {
  if (!projectId) return false;
  return simulatedIdSet(ids).has(projectId);
}

/** Drop legacy localStorage key so machines do not keep a stale private toggle. */
export function clearLegacySimulateUnderContractLocalStorage(): void {
  try {
    window.localStorage.removeItem(SIMULATE_UNDER_CONTRACT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * True only for a project whose real phase is under contract.
 * Simulation never counts — an estimating or preconstruction job stays in that phase.
 */
export function isUnderContractForPlanning(
  project: Pick<TimberProject, 'id' | 'phase'> | null | undefined,
  _opts?: { simulatedProjectIds?: ReadonlySet<string> | readonly string[] },
): boolean {
  if (!project) return false;
  if (isEarlyFunnelProjectPhase(project.phase)) return false;
  return project.phase === 'contracted' || project.phase == null;
}

/** True when this early-funnel project was explicitly selected for the UC overlay. */
export function isSimulatingUnderContract(
  project: Pick<TimberProject, 'id' | 'phase'> | null | undefined,
  simulatedProjectIds: ReadonlySet<string> | readonly string[] | undefined,
): boolean {
  return (
    isEarlyFunnelProjectPhase(project?.phase)
    && isSelectedForSimulation(project?.id, simulatedProjectIds)
  );
}

/**
 * Phase to show in staff planning. Simulation does not promote estimating or
 * preconstruction to under contract (never write this back to Firestore).
 */
export function effectivePhaseForPlanning(
  phase: ProjectPhase | null | undefined,
  _simulatingThisProject?: boolean,
): ProjectPhase {
  if (phase === 'estimating' || phase === 'preconstruction' || phase === 'contracted') return phase;
  return 'contracted';
}

export function isSimulatedUcGroupId(groupId: string | undefined | null): boolean {
  return !!groupId && groupId.startsWith(SIM_UC_GROUP_ID_PREFIX);
}

/** Drop virtual planning group ids before writing an onboarding item to Firestore. */
export function stripSimulatedOnboardingGroupId<T extends { taskGroupId?: string }>(item: T): T {
  if (!isSimulatedUcGroupId(item.taskGroupId)) return item;
  const next = { ...item };
  delete next.taskGroupId;
  return next;
}

function groupNameKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Virtual Design / Procurement / Production / Installation groups for a simulated
 * early-funnel project. Existing real groups are left alone; only missing names
 * are added. IDs are namespaced so they are never written to Firestore.
 */
export function buildSimulatedUnderContractGroups(
  projectId: string,
  existingGroups: TaskGroup[],
): TaskGroup[] {
  const forProject = existingGroups.filter((g) => g.projectId === projectId);
  const names = new Set(forProject.map((g) => groupNameKey(g.name)));
  const extras: TaskGroup[] = [];
  for (const def of SIMULATED_UC_GROUP_DEFS) {
    const want = groupNameKey(def.name);
    if (names.has(want)) continue;
    if (Array.from(names).some((n) => n.includes(want) || want.includes(n))) continue;
    extras.push({
      id: `${SIM_UC_GROUP_ID_PREFIX}${projectId}:${def.key}`,
      projectId,
      name: def.name,
      color: def.color,
      position: def.position,
    });
  }
  return extras;
}

/** Merge real task groups with simulated UC groups for every simulated project. */
export function mergeTaskGroupsForPlanning(
  taskGroups: TaskGroup[],
  projects: Array<Pick<TimberProject, 'id' | 'phase'>>,
  simulatedProjectIds: ReadonlySet<string> | readonly string[] | undefined,
): TaskGroup[] {
  if (!simulatedIdSet(simulatedProjectIds).size) return taskGroups;
  const extras: TaskGroup[] = [];
  for (const p of projects) {
    if (!p.id || !isSimulatingUnderContract(p, simulatedProjectIds)) continue;
    extras.push(...buildSimulatedUnderContractGroups(p.id, taskGroups));
  }
  return extras.length ? [...taskGroups, ...extras] : taskGroups;
}

export function templateItemsToOnboardingItems(
  templateItems: OnboardingTemplateItem[],
): OnboardingItem[] {
  return templateItems.map((t) => ({
    id: t.id,
    title: t.title,
    description: t.description || '',
    category: t.category,
    department: t.department,
    assigneeId: t.defaultAssigneeId || undefined,
    required: t.required ?? true,
    order: t.order,
    onCriticalPath: !!t.onCriticalPath,
    completed: false,
  }));
}

/**
 * Virtual projectOnboarding doc for planning when simulation is on and no real
 * checklist exists yet. Never persisted.
 */
export function buildSimulatedOnboarding(
  projectId: string,
  projectName: string,
  templateItems: OnboardingTemplateItem[],
): ProjectOnboarding {
  const now = new Date();
  return {
    projectId,
    projectName,
    templateVersion: now,
    status: 'active',
    items: templateItemsToOnboardingItems(templateItems),
    createdAt: now,
  };
}

/**
 * Real onboardings win. Simulated projects without a real checklist get a
 * template-backed virtual checklist for staff timeline/task views only.
 */
const DEPENDENCY_EDGE_TYPES: readonly DependencyEdgeType[] = ['FS', 'SS', 'FF', 'SF'];

function parseSimulatedScheduleItem(raw: unknown): SimulatedScheduleItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as SimulatedScheduleItem;
  const next: SimulatedScheduleItem = {};
  if (typeof row.startMs === 'number' && Number.isFinite(row.startMs)) next.startMs = row.startMs;
  if (typeof row.endMs === 'number' && Number.isFinite(row.endMs)) next.endMs = row.endMs;
  if (Array.isArray(row.dependencyEdges)) {
    const edges: TaskDependencyEdge[] = [];
    for (const edge of row.dependencyEdges) {
      if (!edge || typeof edge !== 'object') continue;
      const predecessorId = typeof edge.predecessorId === 'string' ? edge.predecessorId.trim() : '';
      const type = edge.type;
      const lag = edge.lagBusinessDays;
      if (!predecessorId) continue;
      if (!DEPENDENCY_EDGE_TYPES.includes(type)) continue;
      if (typeof lag !== 'number' || !Number.isFinite(lag)) continue;
      edges.push({ predecessorId, type, lagBusinessDays: lag });
    }
    next.dependencyEdges = edges;
  }
  if (next.startMs == null && next.endMs == null && next.dependencyEdges == null) return null;
  return next;
}

export function parseSimulatedSchedules(data: unknown): SimulatedSchedulesByProject {
  if (!data || typeof data !== 'object') return {};
  const raw = (data as StaffPlanningHubDoc).simulatedSchedules;
  if (!raw || typeof raw !== 'object') return {};
  const out: SimulatedSchedulesByProject = {};
  for (const [projectId, byItem] of Object.entries(raw)) {
    const pid = projectId.trim();
    if (!pid || !byItem || typeof byItem !== 'object') continue;
    const items: Record<string, SimulatedScheduleItem> = {};
    for (const [itemId, item] of Object.entries(byItem)) {
      const id = itemId.trim();
      if (!id) continue;
      const parsed = parseSimulatedScheduleItem(item);
      if (parsed) items[id] = parsed;
    }
    if (Object.keys(items).length) out[pid] = items;
  }
  return out;
}

/** Pull date / dependency edits off a checklist patch. Other fields stay virtual. */
export function simulatedScheduleFromItemPatch(
  updates: Partial<OnboardingItem>,
): SimulatedScheduleItem | null {
  const next: SimulatedScheduleItem = {};
  if (updates.startDate instanceof Date && !Number.isNaN(updates.startDate.getTime())) {
    next.startMs = updates.startDate.getTime();
  }
  if (updates.endDate instanceof Date && !Number.isNaN(updates.endDate.getTime())) {
    next.endMs = updates.endDate.getTime();
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'dependencyEdges')) {
    next.dependencyEdges = updates.dependencyEdges ?? [];
  }
  if (next.startMs == null && next.endMs == null && next.dependencyEdges == null) return null;
  return next;
}

/** Copy saved simulation dates onto a checklist row (real or virtual). */
export function withSimulatedSchedule<T extends { id: string }>(
  item: T,
  schedules: Record<string, SimulatedScheduleItem> | undefined,
): T {
  const sched = item.id ? schedules?.[item.id] : undefined;
  if (!sched) return item;
  return {
    ...item,
    ...(typeof sched.startMs === 'number' ? { startDate: new Date(sched.startMs) } : {}),
    ...(typeof sched.endMs === 'number' ? { endDate: new Date(sched.endMs) } : {}),
    ...(sched.dependencyEdges ? { dependencyEdges: sched.dependencyEdges } : {}),
  };
}

export function applySimulatedScheduleToOnboarding(
  onboarding: ProjectOnboarding,
  byItemId: Record<string, SimulatedScheduleItem> | undefined,
): ProjectOnboarding {
  if (!byItemId || !Object.keys(byItemId).length) return onboarding;
  return {
    ...onboarding,
    items: onboarding.items.map((item) => withSimulatedSchedule(item, byItemId)),
  };
}

/**
 * A full template checklist written onto an estimating/preconstruction project
 * by the old simulate-under-contract save. Real contract generation stamps
 * `scheduleNeedsPmReview: true` on every row; this seed does not.
 */
export function isSimulationSeededOnboarding(
  onboarding: ProjectOnboarding | null | undefined,
  templateItems: OnboardingTemplateItem[] | null | undefined,
): boolean {
  if (!onboarding?.items?.length || !templateItems?.length) return false;
  if (onboarding.items.some((item) => item.scheduleNeedsPmReview === true)) return false;
  const templateIds = new Set(templateItems.map((item) => item.id).filter(Boolean));
  if (!templateIds.size || onboarding.items.length !== templateIds.size) return false;
  const seen = new Set<string>();
  for (const item of onboarding.items) {
    if (!item.id || !templateIds.has(item.id) || seen.has(item.id)) return false;
    seen.add(item.id);
  }
  return seen.size === templateIds.size;
}

/** Dates already stored on a seeded checklist, so they survive removing that doc. */
export function simulatedSchedulesFromOnboardingItems(
  items: OnboardingItem[],
): Record<string, SimulatedScheduleItem> {
  const out: Record<string, SimulatedScheduleItem> = {};
  for (const item of items) {
    const patch = simulatedScheduleFromItemPatch({
      startDate: item.startDate,
      endDate: item.endDate,
      ...(item.dependencyEdges ? { dependencyEdges: item.dependencyEdges } : {}),
    });
    if (patch) out[item.id] = patch;
  }
  return out;
}

export function mergeOnboardingsForPlanning(
  realOnboardings: ProjectOnboarding[],
  projects: Array<Pick<TimberProject, 'id' | 'phase' | 'name'>>,
  simulatedProjectIds: ReadonlySet<string> | readonly string[] | undefined,
  templateItems: OnboardingTemplateItem[] | null | undefined,
  simulatedSchedules?: SimulatedSchedulesByProject,
): ProjectOnboarding[] {
  if (!simulatedIdSet(simulatedProjectIds).size || !templateItems?.length) return realOnboardings;
  const have = new Set(
    realOnboardings.map((o) => o.projectId).filter((id): id is string => !!id),
  );
  const extras: ProjectOnboarding[] = [];
  for (const p of projects) {
    if (!p.id || !isSimulatingUnderContract(p, simulatedProjectIds)) continue;
    if (have.has(p.id)) continue;
    const built = buildSimulatedOnboarding(p.id, p.name || 'Project', templateItems);
    extras.push(applySimulatedScheduleToOnboarding(built, simulatedSchedules?.[p.id]));
  }
  return extras.length ? [...realOnboardings, ...extras] : realOnboardings;
}

/** True when this onboarding came from simulation (no Firestore projectOnboarding). */
export function isSimulatedPlanningOnboarding(
  onboarding: ProjectOnboarding | null | undefined,
  realOnboardings: ProjectOnboarding[],
): boolean {
  if (!onboarding?.projectId) return false;
  return !realOnboardings.some((o) => o.projectId === onboarding.projectId);
}
